import {css} from '@emotion/react'
import runAsync from '@/script/runAsync.mjs'
import useSwrNoCache from '@/script/useSwrNoCache.mjs'
import usePermission from '@/components/TreeDoc/plugins/usePermission.mjs'
import useSidePanel from '@/components/TreeDoc/plugins/useSidePanel.mjs'

import {
    Canvas,
    MindMap,
    usePluginAutoScrollCanvas,
    usePluginCancelNodeSelectionOnPointerDownCanvas,
    usePluginCanvas,
    usePluginCanvasSelection,
    usePluginChangeHistory,
    usePluginDescendantCount,
    usePluginDom,
    usePluginDragCanvas,
    usePluginEditNodeText,
    //usePluginEditNodeTextByDoubleClick,
    usePluginEventHandlers,
    usePluginExpandNodeByClickJoint,
    usePluginGodMode,
    usePluginHideNode,
    usePluginHotkey,
    usePluginLogger,
    usePluginMetaData,
    usePluginMindMapPlugins,
    usePluginNodeChange,
    usePluginNodesChange,
    usePluginNodeStyle,
    usePluginNodeText,
    usePluginQuickSelectText,
    usePluginScale,
    usePluginScaleCanvasByWheel,
    usePluginScrollNodeIntoView,
    usePluginSelectionHistory,
    usePluginSelectNodes,
    usePluginSelectNodesByMouse,
    usePluginSelectRootOnDoubleClickCanvas,
    usePluginTreeExtensions,
} from '@/components/MindMap/index.mjs'

import defaultHotkeys from './hotkeys.mjs'
import {SMapDoc} from './SMapDoc.mjs'
import NodeContent from './NodeContent/NodeContent.jsx'
import NodeInnerBox from './NodeInnerBox/NodeInnerBox.jsx'
import DefaultLeftPanel from './SidePanel/LeftPanel/LeftPanel.jsx'
import DefaultRightPanel from './SidePanel/RightPanel/RightPanel.jsx'
import DefaultStatusBar from './StatusBar/StatusBar.jsx'
import useChangeHistory from './plugins/useChangeHistory.mjs'
import useDiffMode from './plugins/useDiffMode.mjs'
import useEditNodeText from './plugins/useEditNodeText.mjs'
import usePluginEditNodeTextByDoubleClick from './plugins/useEditNodeTextByDoubleClick.mjs'
import useEventBus from './plugins/useEventBus.mjs'
import useEventHandlers from './plugins/useEventHandlers.mjs'
import useHideNode from './plugins/useHideNode.mjs'
import useInitMarkedNodes from './plugins/useInitMarkedNodes.mjs'
import useInitSidePanel from './plugins/useInitSidePanel.mjs'
import useInitSelectedNodes from './plugins/useInitSelectedNodes.mjs'
import useMarkNode from './plugins/useMarkNode.mjs'
import useMindMapPlugins from './plugins/useMindMapPlugins.mjs'
import useNodeComment from './plugins/useNodeComment.mjs'
import useNodeContextMenu from './plugins/useNodeContextMenu.jsx'
import useNodeProxy from './plugins/useNodeProxy.mjs'
import useNodeStyle from './plugins/useNodeStyle.mjs'
import useNodeText from './plugins/useNodeText.mjs'
import usePluginSyntax from './plugins/usePluginSyntax.mjs'
import useRunAction from './plugins/useRunAction.mjs'
import useShowNodeDesc from './plugins/useShowNodeDesc.mjs'
import useMindMapExtensions from './plugins/useMindMapExtensions.mjs'

const cssMap = css({
    display: 'grid !important',
    width: '100%',
    height: '100%',
    gridTemplateRows: 'auto 1fr auto',
    gridTemplateColumns: 'auto 1fr auto',

    gridTemplateAreas: `
        "Top    Top    Top"
        "Left   Main   Right"
        "Bottom Bottom Bottom"
    `,

    backgroundColor: '#fff',
    overflow: 'hidden',
    userSelect: 'none',

    '*': {
        '&::-webkit-scrollbar': {
            width: 4,
            height: 4,
        },

        '&::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            backgroundColor: '#c1c1c1',

            '&:hover': {
                backgroundColor: '#a8a8a8',
            },
        },
    },
})

const cssMain = css({
    gridArea: 'Main',
})

const cssLeftPanel = css({
    gridArea: 'Left',
})

const cssRightPanel = css({
    gridArea: 'Right',
})

const cssToolBar = css({
    gridArea: 'Top',
})

const cssStatusBar = css({
    gridArea: 'Bottom',
})

export const BaseMap = ({
    components: {
        LeftPanel = DefaultLeftPanel,
        RightPanel = DefaultRightPanel,
        StatusBar = DefaultStatusBar,
        ToolBar = () => null,
        ...components
    } = {},

    hotkeys,
    initData,

    inits: {
        exec,
        leftPanel = {},
        markedNodes = [],
        rightPanel = {},
        selectedNodes = [],
        showNodeDesc = false,
    } = {},

    permission = [],
    plugins = [],
    readMap,
    onLoad,
    ...props
}) => {
    const handleLoad = map => {
        if (exec) {
            eval(exec)(map)
        }

        onLoad?.(map)
    }

    return (
        <MindMap
            css={cssMap}
            components={{...components, NodeContent, NodeInnerBox}}
            Doc={SMapDoc}
            hotkeys={{...defaultHotkeys, ...hotkeys}}
            initData={initData}

            plugins={[
                usePluginAutoScrollCanvas(),
                usePluginAutoScrollCanvas(),
                usePluginCancelNodeSelectionOnPointerDownCanvas(),
                usePluginCanvas(),
                usePluginCanvasSelection(),
                usePluginChangeHistory(),
                usePluginDescendantCount(),
                usePluginDom(['nodes', 'trees']),
                usePluginDragCanvas(),
                usePluginEditNodeText(),
                usePluginEditNodeTextByDoubleClick(),
                usePluginEventHandlers(),
                usePluginExpandNodeByClickJoint(),
                usePluginGodMode(),
                usePluginHideNode(),
                usePluginHotkey(),
                usePluginLogger(),
                usePluginMetaData(initData.data),
                usePluginMindMapPlugins(),
                usePluginNodeChange(),
                usePluginNodesChange(),
                usePluginNodeStyle(),
                usePluginNodeText(),
                usePluginQuickSelectText(),
                //usePluginScale(),
                //usePluginScaleCanvasByWheel(),
                usePluginScrollNodeIntoView(),
                usePluginSelectionHistory(),
                usePluginSelectNodes(),
                usePluginSelectNodesByMouse(),
                usePluginSelectRootOnDoubleClickCanvas(),
                usePluginTreeExtensions(),
                useMindMapPlugins(),
                useNodeStyle(),
                useNodeText(),
                useMindMapExtensions({readMap}),
                useChangeHistory(),
                useDiffMode(),
                useEditNodeText(),
                useEventBus(),
                useEventHandlers(),
                useHideNode(),
                useInitMarkedNodes(markedNodes),
                useInitSelectedNodes(selectedNodes),
                useInitSidePanel({left: leftPanel, right: rightPanel}),
                useMarkNode(),
                useNodeComment(),
                useNodeContextMenu(),
                useNodeProxy(),
                usePermission(permission),
                usePluginSyntax(),
                useRunAction(),
                useShowNodeDesc(showNodeDesc),
                useSidePanel(),
                ...plugins
            ]}
            onLoad={handleLoad}
            {...props}
        >
            <Canvas css={cssMain} />
            <ToolBar css={cssToolBar} />
            <StatusBar css={cssStatusBar} />
            <LeftPanel css={cssLeftPanel} />
            <RightPanel css={cssRightPanel} />
        </MindMap>
    )
}

const useInitData = readMap => {
    const {data: initData} = useSwrNoCache(
        () => runAsync(readMap, {action: '读取地图'})
    )

    return initData
}

export default function BaseMapWrapper({
    patternRev,
    prjPatternId,
    readMap,
    ...props
}) {
    const initData = useInitData(readMap)

    if (! initData) {
        return null
    }

    if (patternRev || prjPatternId) {
        Object.assign(initData.data, {patternRev, prjPatternId})
    }

    return (
        <BaseMap
            initData={initData}
            readMap={readMap}
            {...props}
        />
    )
}
